import type { ReactNode } from 'react';

import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  AppModuleCode,
  PermissionCode,
  TicketSlaRisk,
  TicketStatus,
  type AuthUser,
  type TicketListQuery,
} from '@gb-ticket/shared-types';

import { AssetsDashboardPanel } from '@/products/helpdesk/features/dashboard/assets-dashboard-panel';
import { DashboardEmptyZoneHint } from '@/products/helpdesk/features/dashboard/dashboard-customize';
import {
  DashboardMoveButtons,
  DashboardWidgetSortableContext,
  SortableDashboardWidget,
  SortableDashboardZone,
} from '@/products/helpdesk/features/dashboard/dashboard-dnd';
import {
  type DashboardLayoutPrefs,
  type DashboardWidgetId,
  type DashboardZoneId,
  visibleDashboardWidgetOrder,
  visibleDashboardZoneOrder,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';
import {
  DashboardPanel,
  DashboardPhaseHint,
  DashboardPlaceholderChart,
} from '@/products/helpdesk/features/dashboard/dashboard-panel';
import {
  DashboardEditHideButton,
  DashboardWidgetEditFrame,
  DashboardZoneShell,
} from '@/products/helpdesk/features/dashboard/dashboard-zone-shell';
import {
  getDashboardWidgetDef,
  getDashboardZoneDef,
} from '@/products/helpdesk/features/dashboard/dashboard-widget-registry';
import { HelpdeskDashboardPanel } from '@/products/helpdesk/features/dashboard/helpdesk-dashboard-panel';
import { SecurityDashboardPanel } from '@/products/helpdesk/features/dashboard/security-dashboard-panel';
import { ServerStorageDashboardPanel } from '@/products/helpdesk/features/dashboard/server-storage-dashboard-panel';
import { TopProblemsDashboardPanel } from '@/products/helpdesk/features/dashboard/top-problems-dashboard-panel';
import { hasPermission, isModuleEnabled } from '@/features/auth/permissions';
import { HomeUpcomingChanges } from '@/products/helpdesk/features/changes/home-upcoming-changes';
import { HomeMajorIncidentsBanner } from '@/products/helpdesk/features/tickets/home-major-incidents-banner';
import {
  HomeTicketQueue,
  OPEN_TICKET_STATUSES,
} from '@/products/helpdesk/features/tickets/home-ticket-queue';
import { ticketListPath } from '@/products/helpdesk/features/tickets/ticket-list-path';
import { cn } from '@/lib/utils';

const DASHBOARD_PAGE_SIZE = 5;

function dashboardQuery(patch: Partial<TicketListQuery>): TicketListQuery {
  return {
    page: 1,
    pageSize: DASHBOARD_PAGE_SIZE,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    ...patch,
  };
}

const ZONE_HEADING_IDS: Record<DashboardZoneId, string> = {
  arbeit: 'dashboard-arbeit-heading',
  lagebild: 'dashboard-lagebild-heading',
};

function isWidgetAvailable(user: AuthUser, id: DashboardWidgetId): boolean {
  return getDashboardWidgetDef(id).isAvailable(user);
}

function ArbeitCardWidget({ id, user }: { id: DashboardWidgetId; user: AuthUser }) {
  const copy = useVoiceCopy();
  const mineFilters = {
    assigneeId: [user.id],
    status: [...OPEN_TICKET_STATUSES],
  };
  const unassignedFilters = {
    unassigned: true as const,
    status: [...OPEN_TICKET_STATUSES],
  };
  const waitingFilters = {
    status: [TicketStatus.WAITING_FOR_CUSTOMER],
  };
  const slaRiskFilters = {
    slaRisk: TicketSlaRisk.AT_RISK,
    status: [...OPEN_TICKET_STATUSES],
    sortBy: 'slaDueAt' as const,
    sortOrder: 'asc' as const,
  };

  switch (id) {
    case 'work-mine':
      return (
        <HomeTicketQueue
          title="Meine offenen Tickets"
          emptyLabel={copy('helpdesk.dashboard.empty-mine')}
          query={dashboardQuery(mineFilters)}
          href={ticketListPath(mineFilters)}
        />
      );
    case 'work-unassigned':
      return (
        <HomeTicketQueue
          title="Nicht zugewiesen"
          emptyLabel="Keine unzugewiesenen Tickets."
          query={dashboardQuery(unassignedFilters)}
          href={ticketListPath(unassignedFilters)}
        />
      );
    case 'work-sla-risk':
      return (
        <HomeTicketQueue
          title="SLA-Risiko"
          emptyLabel="Aktuell kein SLA-Risiko bei offenen Tickets."
          query={dashboardQuery(slaRiskFilters)}
          href={ticketListPath(slaRiskFilters)}
        />
      );
    case 'work-waiting':
      return (
        <HomeTicketQueue
          title="Wartet auf Kunde"
          emptyLabel="Niemand wartet gerade auf eine Kundenrückmeldung."
          query={dashboardQuery(waitingFilters)}
          href={ticketListPath(waitingFilters)}
        />
      );
    case 'work-upcoming-changes':
      return <HomeUpcomingChanges />;
    default:
      return null;
  }
}

function LagebildWidget({ id, user }: { id: DashboardWidgetId; user: AuthUser }) {
  const assetsVisible =
    hasPermission(user, PermissionCode.ASSETS_READ) && isModuleEnabled(user, AppModuleCode.ASSETS);

  switch (id) {
    case 'security':
      return <SecurityDashboardPanel user={user} />;
    case 'assets-licenses':
      return <AssetsDashboardPanel user={user} />;
    case 'helpdesk':
      return <HelpdeskDashboardPanel user={user} />;
    case 'top-problems':
      return <TopProblemsDashboardPanel user={user} />;
    case 'network':
      return (
        <DashboardPanel
          slotId="network"
          title="Netzwerk & Infrastruktur"
          description="Firewall, VPN und Netzgeräte."
        >
          <DashboardPlaceholderChart label="Live-Monitoring folgt" />
          <DashboardPhaseHint>
            Live-Anbindung an Monitoring kommt später — keine Mock-Werte.
          </DashboardPhaseHint>
        </DashboardPanel>
      );
    case 'server-storage':
      return <ServerStorageDashboardPanel user={user} inventarLink={assetsVisible} />;
    default:
      return null;
  }
}

function SortableWidgetGrid({
  zoneId,
  widgetIds,
  editMode,
  gridClassName,
  onHideWidget,
  onMoveWidget,
  renderWidget,
}: {
  zoneId: DashboardZoneId;
  widgetIds: DashboardWidgetId[];
  editMode: boolean;
  gridClassName: string;
  onHideWidget: (zoneId: DashboardZoneId, widgetId: DashboardWidgetId) => void;
  onMoveWidget: (
    zoneId: DashboardZoneId,
    widgetId: DashboardWidgetId,
    direction: 'up' | 'down',
  ) => void;
  renderWidget: (id: DashboardWidgetId) => ReactNode;
}) {
  if (!editMode) {
    return (
      <div className={cn('min-w-0', gridClassName)}>
        {widgetIds.map((id) => (
          <div key={id} className="min-w-0">
            {renderWidget(id)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <DashboardWidgetSortableContext zoneId={zoneId} widgetIds={widgetIds}>
      <div className={cn('min-w-0', gridClassName)}>
        {widgetIds.map((id, index) => {
          const title = getDashboardWidgetDef(id).title;
          return (
            <SortableDashboardWidget key={id} zoneId={zoneId} widgetId={id} title={title}>
              {({ dragHandle }) => (
                <DashboardWidgetEditFrame
                  editMode
                  hideLabel={`${title} ausblenden`}
                  onHide={() => onHideWidget(zoneId, id)}
                  dragHandle={dragHandle}
                  moveControls={
                    <DashboardMoveButtons
                      label={title}
                      canUp={index > 0}
                      canDown={index < widgetIds.length - 1}
                      onUp={() => onMoveWidget(zoneId, id, 'up')}
                      onDown={() => onMoveWidget(zoneId, id, 'down')}
                    />
                  }
                >
                  {renderWidget(id)}
                </DashboardWidgetEditFrame>
              )}
            </SortableDashboardWidget>
          );
        })}
      </div>
    </DashboardWidgetSortableContext>
  );
}

export function DashboardZone({
  zoneId,
  user,
  layout,
  editMode,
  onHideZone,
  onHideWidget,
  onMoveZone,
  onMoveWidget,
  onStartCustomize,
}: {
  zoneId: DashboardZoneId;
  user: AuthUser;
  layout: DashboardLayoutPrefs;
  editMode: boolean;
  onHideZone: (zoneId: DashboardZoneId) => void;
  onHideWidget: (zoneId: DashboardZoneId, widgetId: DashboardWidgetId) => void;
  onMoveZone: (zoneId: DashboardZoneId, direction: 'up' | 'down') => void;
  onMoveWidget: (
    zoneId: DashboardZoneId,
    widgetId: DashboardWidgetId,
    direction: 'up' | 'down',
  ) => void;
  onStartCustomize: () => void;
}) {
  const zoneDef = getDashboardZoneDef(zoneId);
  const order = visibleDashboardWidgetOrder(layout, zoneId).filter((id) =>
    isWidgetAvailable(user, id),
  );
  const visibleZones = visibleDashboardZoneOrder(layout);
  const zoneIndex = visibleZones.indexOf(zoneId);

  const headerAction = editMode ? (
    <DashboardEditHideButton
      label={`${zoneDef.title} ausblenden`}
      onClick={() => onHideZone(zoneId)}
    />
  ) : null;

  const moveControls =
    editMode && visibleZones.length > 1 ? (
      <DashboardMoveButtons
        label={zoneDef.title}
        canUp={zoneIndex > 0}
        canDown={zoneIndex >= 0 && zoneIndex < visibleZones.length - 1}
        onUp={() => onMoveZone(zoneId, 'up')}
        onDown={() => onMoveZone(zoneId, 'down')}
      />
    ) : null;

  const body =
    zoneId === 'arbeit' ? (
      (() => {
        const showBanner = order.includes('work-major-incidents');
        const cards = order.filter((id) => id !== 'work-major-incidents');
        return cards.length === 0 && !showBanner ? (
          <DashboardEmptyZoneHint onCustomize={onStartCustomize} />
        ) : (
          <SortableWidgetGrid
            zoneId="arbeit"
            widgetIds={cards}
            editMode={editMode}
            gridClassName="grid gap-4 lg:grid-cols-2"
            onHideWidget={onHideWidget}
            onMoveWidget={onMoveWidget}
            renderWidget={(id) => <ArbeitCardWidget id={id} user={user} />}
          />
        );
      })()
    ) : order.length === 0 ? (
      <DashboardEmptyZoneHint onCustomize={onStartCustomize} />
    ) : (
      <SortableWidgetGrid
        zoneId="lagebild"
        widgetIds={order}
        editMode={editMode}
        gridClassName="grid gap-4 md:grid-cols-2 xl:grid-cols-3"
        onHideWidget={onHideWidget}
        onMoveWidget={onMoveWidget}
        renderWidget={(id) => <LagebildWidget id={id} user={user} />}
      />
    );

  const beforeHeader =
    zoneId === 'arbeit' && order.includes('work-major-incidents') ? (
      <DashboardWidgetEditFrame
        editMode={editMode}
        hideLabel={`${getDashboardWidgetDef('work-major-incidents').title} ausblenden`}
        onHide={() => onHideWidget('arbeit', 'work-major-incidents')}
      >
        <HomeMajorIncidentsBanner />
      </DashboardWidgetEditFrame>
    ) : null;

  if (!editMode) {
    return (
      <DashboardZoneShell
        zoneId={zoneId}
        title={zoneDef.title}
        description={zoneId === 'lagebild' ? zoneDef.description : undefined}
        headingId={ZONE_HEADING_IDS[zoneId]}
        beforeHeader={beforeHeader}
      >
        {body}
      </DashboardZoneShell>
    );
  }

  return (
    <SortableDashboardZone zoneId={zoneId} title={zoneDef.title}>
      {({ setNodeRef, style, isDragging, dragHandle }) => (
        <DashboardZoneShell
          zoneId={zoneId}
          title={zoneDef.title}
          description={zoneId === 'lagebild' ? zoneDef.description : undefined}
          headingId={ZONE_HEADING_IDS[zoneId]}
          editMode
          setNodeRef={setNodeRef}
          style={style}
          isDragging={isDragging}
          dragHandle={dragHandle}
          moveControls={moveControls}
          headerAction={headerAction}
          beforeHeader={beforeHeader}
        >
          {body}
        </DashboardZoneShell>
      )}
    </SortableDashboardZone>
  );
}
